import {
  Add,
  AdminPanelSettings,
  Category,
  Chat,
  CollectionsBookmark,
  Home,
  MenuBook,
  Search,
  Settings,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { Link, NavLink, Outlet } from "react-router";
import type { GetApiAuthCapabilitiesResponse } from "@/api/generated/types.gen";
import { SessionList } from "@/domain/chat/components/session-list";
import { useCapabilities } from "@/hooks/use-capabilities";
import logo from "./assets/logo-ilpt.svg";

type NavItemType = {
  to?: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  capability?: keyof GetApiAuthCapabilitiesResponse;
};

const navItems: NavItemType[] = [
  { to: "/", label: "Home", icon: <Home />, end: true },
  { label: "Browse", icon: <Search /> },
  { to: "/collections", label: "Collections", icon: <CollectionsBookmark /> },
];

const actionItems: NavItemType[] = [
  { to: "/assistants/manage", label: "Manage Assistants", icon: <Settings /> },
  { to: "/assistants/new", label: "Create Assistant", icon: <Add /> },
  {
    to: "/admin/collections/manage",
    label: "Admin",
    icon: <AdminPanelSettings />,
    capability: "canManageCollections",
  },
  {
    to: "/admin/kinds/manage",
    label: "Manage Kinds",
    icon: <Category />,
    capability: "canManageAssistantKinds",
  },
  { label: "Word Manual", icon: <MenuBook /> },
  { label: "Feedback", icon: <Chat /> },
];

function NavItem({ item }: { item: NavItemType }) {
  const sx = {
    "&.active": {
      bgcolor: "primary.light",
      color: "primary.main",
      "& .MuiListItemIcon-root": {
        color: "primary.main",
      },
    },
  };

  if (item.to) {
    return (
      <ListItemButton component={NavLink} to={item.to} end={item.end} sx={sx}>
        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    );
  }

  return (
    <ListItemButton disabled>
      <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} />
    </ListItemButton>
  );
}

export default function RootLayout() {
  const { capabilities, isLoading } = useCapabilities();

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "row" }}>
      {/* Sidebar */}
      <Box
        component="aside"
        sx={{
          width: 240,
          minWidth: 240,
          bgcolor: "background.paper",
          borderRight: 1,
          borderColor: "divider",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          height: "100vh",
          position: "sticky",
          top: 0,
        }}
      >
        {/* Logo */}
        <Box
          component={Link}
          to="/"
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          <Box component="img" src={logo} alt="ILPT Logo" sx={{ height: 32, width: "auto" }} />
        </Box>

        {/* Main navigation */}
        <List component="nav" disablePadding>
          {navItems.map((item) => (
            <NavItem key={item.label} item={item} />
          ))}
        </List>

        {/* Chats section */}
        <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              display: "block",
              mb: 1,
            }}
          >
            Chats
          </Typography>
          <SessionList />
        </Box>

        {/* Action links */}
        <Divider />
        <List component="nav" disablePadding>
          {actionItems.map((item) => {
            if (!item.capability) {
              return <NavItem key={item.label} item={item} />;
            }
            if (isLoading) {
              return (
                <Skeleton
                  key={item.label}
                  variant="rectangular"
                  height={48}
                  sx={{ mx: 1, my: 0.5, borderRadius: 1 }}
                />
              );
            }
            if (capabilities[item.capability]) {
              return <NavItem key={item.label} item={item} />;
            }
            return null;
          })}
        </List>

        {/* User profile */}
        <Divider />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}>
          <Avatar sx={{ width: 32, height: 32 }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" fontWeight={500}>
              User Name
            </Typography>
            <Typography variant="caption" color="text.secondary">
              user@example.com
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          minHeight: "100vh",
        }}
      >
        {/* Search header */}
        <Box
          component="header"
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "action.hover",
              borderRadius: 2,
              px: 2,
              py: 1,
              maxWidth: 600,
            }}
          >
            <Search color="action" />
            <InputBase placeholder="Search..." sx={{ flex: 1, fontSize: "0.875rem" }} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" sx={{ minWidth: "auto", px: 1.5 }}>
                All
              </Button>
              <Button size="small" variant="outlined" sx={{ minWidth: "auto", px: 1.5 }}>
                Filters
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Content outlet */}
        <Box component="main" sx={{ flex: 1, p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
